import { format } from "date-fns";
import { any, partial, pick } from "ramda";
import { ThunkAction } from "redux-thunk";
import { Maybe } from "true-myth";
import { default as makeUuid } from "uuid/v4";

import { IRootStateProps, SchedulingApiRequests } from "types/reducers";
import { RequestStatus } from "types/requests";
import {
  ISchedule,
  IPeople,
  IPosUser,
  IUnavailabilityBlock,
  Frequency,
} from "types/schedules";

import { trackEvent } from "helpers/analytics";
import { DateFormats } from "helpers/dates";
import { generateNotifyBody } from "helpers/schedule/scheduling_actions";
import {
  NotifyType,
  posUsersFromPeopleWithShifts,
} from "helpers/schedule/scheduling_page";
import { dedupeShifts } from "helpers/schedule/shifts";
import { posUsersToPeople } from "helpers/transforms/pos_users";
import {
  matchRoleCategories,
  ITransformedResponse,
} from "helpers/transforms/schedule";

import {
  getSavedSchedule,
  getPublishedSchedule,
  postDraftSchedule,
  postPublishSchedule,
  INotifyBody,
  postNotifyPublishedSchedule,
  IScheduleNotifyResponse,
} from "interfaces/schedule";
import {
  getPosUsers,
  getUnavailability,
  postUnavailability,
  putUnavailability,
  requestDeleteUnavailability,
} from "interfaces/pos_users";

import { selectPermalink } from "store/select/account";
import {
  selectUnavailByUuid,
  selectStartEndDates,
  selectScheduling,
  selectRawSchedule,
} from "store/select/scheduling";
import { syncUnavailsToDateRange } from "helpers/transforms/blockDates";

type UnavailActionTypes = IReceiveUnavailsAction | IRemoveUnavailAction;

type SchedulingActionTypes =
  | IReceivePosUsersAction
  | IReceivePublicPublishedScheduleAction
  | IReceivePublishedScheduleAction
  | IReceiveSavedScheduleAction
  | IReceiveSavedDraftAction
  | IReceiveTemplateScheduleAction
  | ISetAutosaveIntervalIdAction
  | ISetRequestStatusAction
  | ISetScheduleDateBoundariesAction
  | UnavailActionTypes
  | IToggleNotifyModal;

type SchedulingThunkResult<R> = ThunkAction<
  R,
  IRootStateProps,
  undefined,
  SchedulingActionTypes
>;

enum SchedulingTypeKeys {
  RECEIVE_POS_USERS = "SCHEDULING/RECEIVE_POS_USERS",
  RECEIVE_PUBLIC_PUBLISHED_SCHEDULE = "SCHEDULING/RECEIVE_PUBLIC_PUBLISHED_SCHEDULE",
  RECEIVE_PUBLISHED_SCHEDULE = "SCHEDULING/RECEIVE_PUBLISHED_SCHEDULE",
  RECEIVE_SAVED_SCHEDULE = "SCHEDULING/RECEIVE_SAVED_SCHEDULE",
  RECEIVE_DRAFT_SCHEDULE = "SCHEDULING/RECEIVE_DRAFT_SCHEDULE",
  RECEIVE_TEMPLATE_SCHEDULE = "SCHEDULING/RECEIVE_TEMPLATE_SCHEDULE",
  RECEIVE_UNAVAILABILITY_BLOCKS = "SCHEDULING/RECEIVE_UNAVAILABILITY_BLOCKS",
  REMOVE_UNAVAILABILITY_BLOCK = "SCHEDULING/REMOVE_UNAVAILABILITY_BLOCK",
  SET_AUTOSAVE_INTERVAL_ID = "SCHEDULING/SET_AUTOSAVE_INTERVAL_ID",
  SET_REQUEST_STATUS = "SCHEDULING/SET_REQUEST_STATUS",
  SET_SAVED_UPDATED_AT = "SCHEDULING/SET_SAVED_UPDATED_AT",
  SET_SCHEDULE_DATE_BOUNDARIES = "SCHEDULING/SET_SCHEDULE_DATE_BOUNDARIES",
  TOGGLE_NOTIFY_MODAL = "SCHEDULING/TOGGLE_NOTIFY_MODAL",
}

interface ISetScheduleDateBoundariesAction {
  type: SchedulingTypeKeys.SET_SCHEDULE_DATE_BOUNDARIES;
  startDate: Date;
  endDate: Date;
}

function setScheduleDateBoundaries(
  startDate: Date,
  endDate: Date
): ISetScheduleDateBoundariesAction {
  return {
    type: SchedulingTypeKeys.SET_SCHEDULE_DATE_BOUNDARIES,
    startDate,
    endDate,
  };
}

enum MODAL_STATUS {
  SHOW,
  HIDE,
}

interface IToggleNotifyModal {
  type: SchedulingTypeKeys.TOGGLE_NOTIFY_MODAL;
  modalAction: MODAL_STATUS;
}

function toggleNotifyModal(status: MODAL_STATUS): IToggleNotifyModal {
  return {
    type: SchedulingTypeKeys.TOGGLE_NOTIFY_MODAL,
    modalAction: status,
  };
}

interface IReceiveSavedScheduleAction {
  type: SchedulingTypeKeys.RECEIVE_SAVED_SCHEDULE;
  savedSchedule: ITransformedResponse[];
  options: IReceiveSavedScheduleOpts;
}
interface IReceiveSavedScheduleOpts {
  overwrite?: boolean;
}

function receiveSavedSchedule(
  savedSchedule: ITransformedResponse[],
  options: IReceiveSavedScheduleOpts = {}
): IReceiveSavedScheduleAction {
  return {
    type: SchedulingTypeKeys.RECEIVE_SAVED_SCHEDULE,
    savedSchedule,
    options,
  };
}

interface IReceivePublishedScheduleAction {
  type: SchedulingTypeKeys.RECEIVE_PUBLISHED_SCHEDULE;
  publishedSchedule: ITransformedResponse[];
}

function receivePublishedSchedule(
  publishedSchedule: ITransformedResponse[]
): IReceivePublishedScheduleAction {
  return {
    type: SchedulingTypeKeys.RECEIVE_PUBLISHED_SCHEDULE,
    publishedSchedule,
  };
}

interface IReceivePublicPublishedScheduleAction {
  type: SchedulingTypeKeys.RECEIVE_PUBLIC_PUBLISHED_SCHEDULE;
  publishedSchedule: ITransformedResponse[];
}

function receivePublicPublishedSchedule(
  publishedSchedule: ITransformedResponse[]
): IReceivePublicPublishedScheduleAction {
  return {
    type: SchedulingTypeKeys.RECEIVE_PUBLIC_PUBLISHED_SCHEDULE,
    publishedSchedule,
  };
}

interface IReceiveTemplateScheduleAction {
  type: SchedulingTypeKeys.RECEIVE_TEMPLATE_SCHEDULE;
  templateSchedule: ISchedule;
}

function receiveTemplateSchedule(
  templateSchedule: ISchedule
): SchedulingThunkResult<void> {
  return (dispatch, getState) => {
    // Infer Role Category information by comparing to known people records
    const { users } = getState().scheduling;
    const people = posUsersToPeople(users);
    templateSchedule = matchRoleCategories(templateSchedule, people);
    dispatch({
      type: SchedulingTypeKeys.RECEIVE_TEMPLATE_SCHEDULE,
      templateSchedule,
    });
  };
}

interface IReceivePosUsersAction {
  type: SchedulingTypeKeys.RECEIVE_POS_USERS;
  posUsers: IPosUser[];
}

function receivePosUsers(posUsers: IPosUser[]): IReceivePosUsersAction {
  return {
    type: SchedulingTypeKeys.RECEIVE_POS_USERS,
    posUsers,
  };
}

interface IReceiveUnavailsAction {
  type: SchedulingTypeKeys.RECEIVE_UNAVAILABILITY_BLOCKS;
  blocks: IUnavailabilityBlock[];
}

function receiveUnavails(
  blocks: IUnavailabilityBlock[]
): IReceiveUnavailsAction {
  return {
    type: SchedulingTypeKeys.RECEIVE_UNAVAILABILITY_BLOCKS,
    blocks,
  };
}

interface IRemoveUnavailAction {
  type: SchedulingTypeKeys.REMOVE_UNAVAILABILITY_BLOCK;
  uuid: string;
}

function removeUnavail(uuid: string): IRemoveUnavailAction {
  return {
    type: SchedulingTypeKeys.REMOVE_UNAVAILABILITY_BLOCK,
    uuid,
  };
}

function fetchDraftSchedule(
  startDay: Date,
  endDay: Date,
  permalink: string
): SchedulingThunkResult<void> {
  const startDateParam: string = format(startDay, DateFormats.YEAR_MONTH_DAY);
  const endDateParam: string = format(endDay, DateFormats.YEAR_MONTH_DAY);

  return (dispatch): Promise<void> => {
    dispatch(setDraftScheduleGetRequestStatus(RequestStatus.REQUESTING));

    return getSavedSchedule(startDateParam, endDateParam, permalink)
      .then((schedule: ITransformedResponse[]) => {
        dispatch(receiveSavedSchedule(schedule));
        dispatch(setDraftScheduleGetRequestStatus(RequestStatus.SUCCESS));
      })
      .catch((err) => {
        console.error(
          `fetchDraftSchedule request failed!
          ${permalink} startDateParam:${startDateParam} endDateParam:${endDateParam}`
        );
        dispatch(setDraftScheduleGetRequestStatus(RequestStatus.FAILURE));
        reportError(
          `getSavedSchedule request failed! - ${err}
          permalink:${permalink} startDateParam:${startDateParam} endDateParam:${endDateParam}`
        );
      });
  };
}

function fetchPublishedSchedule(
  startDay: Date,
  endDay: Date,
  permalink: string,
  forPublic: boolean
): SchedulingThunkResult<void> {
  const startDateParam: string = format(startDay, DateFormats.YEAR_MONTH_DAY);
  const endDateParam: string = format(endDay, DateFormats.YEAR_MONTH_DAY);

  return (dispatch): Promise<void> => {
    dispatch(setPublishedScheduleRequestStatus(RequestStatus.REQUESTING));

    return getPublishedSchedule(startDateParam, endDateParam, permalink)
      .then((schedule: ITransformedResponse[]) => {
        if (forPublic) {
          dispatch(receivePublicPublishedSchedule(schedule));
        } else {
          const isPopulated = any((day) => !!day.people.length, schedule);
          if (isPopulated) {
            dispatch(receivePublishedSchedule(schedule));
          }
        }

        dispatch(setPublishedScheduleRequestStatus(RequestStatus.SUCCESS));
      })
      .catch((err) => {
        console.error(
          `fetchPublishedSchedule request failed! ${permalink} startDateParam:${startDateParam}
          endDateParam:${endDateParam} forPublic:${forPublic}`
        );
        dispatch(setPublishedScheduleRequestStatus(RequestStatus.FAILURE));
        reportError(
          `getPublishedSchedule request failed! - ${err}
          permalink:${permalink} startDateParam:${startDateParam}
          endDateParam:${endDateParam} forPublic:${forPublic}`
        );
      });
  };
}

function fetchPosUsers(
  permalink: string,
  prettyUrl: string
): SchedulingThunkResult<void> {
  return (dispatch) => {
    dispatch(setPosUsersRequestStatus(RequestStatus.REQUESTING));

    return getPosUsers(permalink, prettyUrl)
      .then((posUsers) => {
        dispatch(receivePosUsers(posUsers));
        dispatch(setPosUsersRequestStatus(RequestStatus.SUCCESS));
      })
      .catch((err) => {
        console.error(`fetchPosUsers request failed! ${permalink}`);
        dispatch(setPosUsersRequestStatus(RequestStatus.FAILURE));
        reportError(`getPosUsers request failed! - ${err}
        permalink:${permalink}`);
      });
  };
}

function fetchUnavail(permalink: string): SchedulingThunkResult<void> {
  return (dispatch, getState) => {
    const { startDate, endDate } = selectStartEndDates(getState());
    return Maybe.all(startDate, endDate).match({
      Just: ([startDateUnwrapped, endDateUnwrapped]) => {
        dispatch(setUnavailabilityGetRequestStatus(RequestStatus.REQUESTING));

        return getUnavailability(
          format(startDateUnwrapped, DateFormats.YEAR_MONTH_DAY),
          format(endDateUnwrapped, DateFormats.YEAR_MONTH_DAY),
          permalink
        )
          .then((unavails: IUnavailabilityBlock[]) => {
            // Recurring unavails need to be synced to fit within the current week.
            // For instance, they have a `date` of 7/02 but are being viewed during the week of 7/09
            const syncedUnavails = syncUnavailsToDateRange(
              unavails,
              startDate,
              endDate
            );
            dispatch(receiveUnavails(syncedUnavails));
            dispatch(setUnavailabilityGetRequestStatus(RequestStatus.SUCCESS));
          })
          .catch((err) => {
            dispatch(setUnavailabilityGetRequestStatus(RequestStatus.FAILURE));
            reportError(
              `getUnavailability request failed! - ${err}
              permalink:${permalink}`
            );
          });
      },
      Nothing: () => Promise.reject(),
    });
  };
}

function saveUnavail(
  unavail: IUnavailabilityBlock
): SchedulingThunkResult<void> {
  return (dispatch, getState) => {
    const isUpdate = !!unavail.uuid;
    if (isUpdate) {
      // Make sure we're not waiting on an existing POST.
      const { requestStatuses } = selectScheduling(getState());
      if (requestStatuses.unavailPost === RequestStatus.REQUESTING) {
        // If so, retry
        window.setTimeout(() => {
          dispatch(saveUnavail(unavail));
        }, 250);
        return;
      }
    }

    dispatch(setUnavailabilityPostRequestStatus(RequestStatus.REQUESTING));

    // Optimistically insert new unavail to Redux
    // Look for a stale record if one exists so it's ready for revert
    const staleRecord = selectUnavailByUuid(unavail.uuid)(getState());
    let optimisticRecord: IUnavailabilityBlock = {
      ...unavail,
      uuid: unavail.uuid || makeUuid(),
    };
    if (staleRecord && staleRecord.frequency === Frequency.WEEKLY) {
      // Don't update date and times for recurring unavails
      const maintainedDates = pick(
        ["date", "startTime", "endTime"],
        staleRecord
      );
      optimisticRecord = {
        ...optimisticRecord,
        ...maintainedDates,
      };
    }
    dispatch(receiveUnavails([optimisticRecord]));

    const apiAction = isUpdate ? putUnavailability : postUnavailability;
    const permalink = selectPermalink(getState()).unwrapOr("");
    return apiAction(optimisticRecord, permalink)
      .then((savedUnavail: IUnavailabilityBlock) => {
        const frequency =
          savedUnavail.frequency === Frequency.ONCE
            ? "(one time)"
            : "(weekly recurring)";
        trackEvent(`create an unavailability shift ${frequency}`);
        dispatch(receiveUnavails([savedUnavail]));
        dispatch(setUnavailabilityPostRequestStatus(RequestStatus.SUCCESS));
      })
      .catch((err) => {
        dispatch(setUnavailabilityPostRequestStatus(RequestStatus.FAILURE));
        // revert optimism
        if (staleRecord) {
          dispatch(receiveUnavails([staleRecord]));
        } else {
          dispatch(removeUnavail(optimisticRecord.uuid));
        }
        // Pull canonical data
        dispatch(fetchUnavail(permalink));
        reportError(`saveUnavail request failed! - ${err}
        permalink:${permalink}`);
      });
  };
}

function saveUnavailException(
  exception: IUnavailabilityBlock
): SchedulingThunkResult<void> {
  return (dispatch, getState) => {
    if (!exception.parentId) {
      // Exceptions must point back at their recurring parent
      return Promise.reject();
    }
    dispatch(setUnavailabilityPostRequestStatus(RequestStatus.REQUESTING));

    // Optimistically remove the corresponding recurring record from redux
    dispatch(removeUnavail(exception.parentId));
    const permalink = selectPermalink(getState()).unwrapOr("");
    return postUnavailability(exception, permalink)
      .then(() => {
        trackEvent(
          `delete unavailability (create single day exception in weekly recurring)`
        );
        dispatch(setUnavailabilityPostRequestStatus(RequestStatus.SUCCESS));
      })
      .catch((err) => {
        dispatch(setUnavailabilityPostRequestStatus(RequestStatus.FAILURE));
        // Pull canonical data on failure
        dispatch(fetchUnavail(permalink));
        reportError(
          `postUnavailability request failed! - ${err}
          permalink:${permalink}`
        );
      });
  };
}

function deleteUnavail(
  unavail: IUnavailabilityBlock
): SchedulingThunkResult<void> {
  return (dispatch, getState) => {
    dispatch(setUnavailabilityDeleteRequestStatus(RequestStatus.REQUESTING));
    dispatch(removeUnavail(unavail.uuid)); // optimistic
    const permalink = selectPermalink(getState()).unwrapOr("");

    return requestDeleteUnavailability(unavail.uuid, permalink)
      .then(() => {
        const frequency =
          unavail.frequency === Frequency.ONCE
            ? "(one time)"
            : "(weekly recurring)";
        trackEvent(`delete an unavailability shift ${frequency}`);
        dispatch(setUnavailabilityDeleteRequestStatus(RequestStatus.SUCCESS));
      })
      .catch((err) => {
        dispatch(setUnavailabilityDeleteRequestStatus(RequestStatus.FAILURE));
        // revert optimism
        dispatch(receiveUnavails([unavail]));
        // Pull canonical data
        dispatch(fetchUnavail(permalink));
        reportError(
          `requestDeleteUnavailability failed! - ${err}
          permalink:${permalink}`
        );
      });
  };
}

interface ISetAutosaveIntervalIdAction {
  type: SchedulingTypeKeys.SET_AUTOSAVE_INTERVAL_ID;
  autoSaveIntervalId: number;
}

function autoSaveDraft(): SchedulingThunkResult<void> {
  return (dispatch, getState) => {
    const { autoSaveIntervalId } = selectScheduling(getState());
    if (autoSaveIntervalId) {
      // Autosave is already running, don't start again
      return;
    } else {
      const intervalId = window.setInterval(() => {
        // Check every 200ms if we need to save the draft
        dispatch(saveDraftSchedule());
      }, 200);
      dispatch({
        type: SchedulingTypeKeys.SET_AUTOSAVE_INTERVAL_ID,
        autoSaveIntervalId: intervalId,
      });
    }
  };
}

function cancelAutoSaveDraft(): SchedulingThunkResult<void> {
  return (dispatch, getState) => {
    const { autoSaveIntervalId } = selectScheduling(getState());
    if (autoSaveIntervalId) {
      window.clearInterval(autoSaveIntervalId);
      dispatch({
        type: SchedulingTypeKeys.SET_AUTOSAVE_INTERVAL_ID,
        autoSaveIntervalId: 0,
      });
    }
  };
}

interface IReceiveSavedDraftAction {
  type: SchedulingTypeKeys.RECEIVE_DRAFT_SCHEDULE;
  draft: ISchedule;
}

function receiveSavedDraft(draft: ISchedule): IReceiveSavedDraftAction {
  return {
    type: SchedulingTypeKeys.RECEIVE_DRAFT_SCHEDULE,
    draft,
  };
}

function saveDraftSchedule(forced?: boolean): SchedulingThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const { draft, schedule, requestStatuses } = selectScheduling(state);
    const permalink = selectPermalink(state).unwrapOr("");

    // Skip this save action if:
    // - Permalink is not yet available in state
    // - Draft already equals schedule (no new changes have happened since last tick)
    //   - Unless forced
    // - We're waiting for API to get initial saved schedule
    // - We're waiting for API from last tick (still saving a draft)
    if (
      !permalink ||
      (draft.equals(Maybe.of(schedule)) && !forced) ||
      requestStatuses.draftScheduleGet === RequestStatus.REQUESTING ||
      requestStatuses.draftSchedulePost === RequestStatus.REQUESTING
    ) {
      return Promise.resolve();
    }

    // Remove any duplicated shifts before saving the draft
    // This was consequence of a bug on 5/30/2019
    // https://github.com/upserve/hq_workforce_ui/pull/174/files
    const dedupedSchedule = dedupeShifts(schedule);

    dispatch(setDraftSchedulePostRequestStatus(RequestStatus.REQUESTING));
    return postDraftSchedule(permalink, dedupedSchedule)
      .then((updatedSchedule) => {
        dispatch(setDraftSchedulePostRequestStatus(RequestStatus.SUCCESS));

        // Check if there have been new changes to schedule while waiting for API
        // If not, replace it and draft with fresh updatedSchedule data
        const currentSchedule = selectScheduling(getState()).schedule;
        if (currentSchedule === schedule) {
          // No changes, replace
          dispatch(receiveSavedSchedule(updatedSchedule, { overwrite: true }));
        } else {
          // Otherwise just set the draft to match what was sent to API.
          // Next tick will POST any new changes
          dispatch(receiveSavedDraft(schedule));
        }
      })
      .catch((err) => {
        dispatch(setDraftSchedulePostRequestStatus(RequestStatus.FAILURE));
        reportError(
          `postDraftSchedule request failed! - ${err}
          permalink:${permalink}`
        );
      });
  };
}

function publishSchedule(): SchedulingThunkResult<void> {
  return (dispatch, getState) => {
    const permalink = selectPermalink(getState()).unsafelyUnwrap();
    const schedule = selectRawSchedule(getState());
    dispatch(toggleNotifyModal(MODAL_STATUS.SHOW));
    dispatch(setPublishSchedulePostRequestStatus(RequestStatus.REQUESTING));
    // Kick off a draft save to keep two versions in sync
    dispatch(saveDraftSchedule(true));

    // Remove any duplicated shifts before saving the draft
    // This was consequence of a bug on 5/30/2019
    // https://github.com/upserve/hq_workforce_ui/pull/174/files
    const dedupedSchedule = dedupeShifts(schedule);

    return postPublishSchedule(permalink, dedupedSchedule)
      .then((updatedSchedule: ITransformedResponse[]) => {
        dispatch(receivePublishedSchedule(updatedSchedule));
        dispatch(setPublishSchedulePostRequestStatus(RequestStatus.SUCCESS));
      })
      .catch((err) => {
        dispatch(setPublishSchedulePostRequestStatus(RequestStatus.FAILURE));
        reportError(`postPublishSchedule request failed! - ${err}
        ${permalink}`);
      });
  };
}

function notifyPosUsers(
  permalink: string,
  startDate: Date,
  endDate: Date,
  people: IPeople,
  notifyType: NotifyType
): SchedulingThunkResult<void> {
  return (dispatch) => {
    const posUsers: IPosUser[] = posUsersFromPeopleWithShifts(people);
    const notify: INotifyBody = generateNotifyBody(notifyType, posUsers);

    dispatch(setNotifySchedulePostRequestStatus(RequestStatus.REQUESTING));
    return postNotifyPublishedSchedule(
      permalink,
      format(startDate, DateFormats.YEAR_MONTH_DAY),
      format(endDate, DateFormats.YEAR_MONTH_DAY),
      notify
    )
      .then((notifyResponse: IScheduleNotifyResponse) => {
        dispatch(setNotifySchedulePostRequestStatus(RequestStatus.SUCCESS));
        dispatch(toggleNotifyModal(MODAL_STATUS.HIDE));
      })
      .catch((err) => {
        dispatch(setNotifySchedulePostRequestStatus(RequestStatus.FAILURE));
        dispatch(toggleNotifyModal(MODAL_STATUS.HIDE));
        reportError(
          `postNotifyPublishedSchedule request failed! - ${err}
          permalink:${permalink}`
        );
      });
  };
}

// API Requests:
interface ISetRequestStatusAction {
  type: SchedulingTypeKeys.SET_REQUEST_STATUS;
  status: RequestStatus;
  apiRequest: SchedulingApiRequests;
}

function setApiRequest(
  apiRequest: SchedulingApiRequests,
  status: RequestStatus
): ISetRequestStatusAction {
  return {
    type: SchedulingTypeKeys.SET_REQUEST_STATUS,
    status,
    apiRequest,
  };
}

const setNotifySchedulePostRequestStatus = partial(setApiRequest, [
  SchedulingApiRequests.NOTIFY_POST,
]);
const setPosUsersRequestStatus = partial(setApiRequest, [
  SchedulingApiRequests.POS_USERS_GET,
]);
const setPublishedScheduleRequestStatus = partial(setApiRequest, [
  SchedulingApiRequests.PUBLISHED_GET,
]);
const setPublishSchedulePostRequestStatus = partial(setApiRequest, [
  SchedulingApiRequests.PUBLISH_POST,
]);
const setDraftScheduleGetRequestStatus = partial(setApiRequest, [
  SchedulingApiRequests.DRAFT_GET,
]);
const setDraftSchedulePostRequestStatus = partial(setApiRequest, [
  SchedulingApiRequests.DRAFT_POST,
]);
const setUnavailabilityGetRequestStatus = partial(setApiRequest, [
  SchedulingApiRequests.UNAVAIL_GET,
]);
const setUnavailabilityPostRequestStatus = partial(setApiRequest, [
  SchedulingApiRequests.UNAVAIL_POST,
]);
const setUnavailabilityDeleteRequestStatus = partial(setApiRequest, [
  SchedulingApiRequests.UNAVAIL_DELETE,
]);

export {
  fetchPosUsers,
  fetchDraftSchedule,
  fetchPublishedSchedule,
  fetchUnavail,
  autoSaveDraft,
  cancelAutoSaveDraft,
  publishSchedule,
  receiveTemplateSchedule,
  saveDraftSchedule,
  saveUnavail,
  saveUnavailException,
  deleteUnavail,
  SchedulingTypeKeys,
  SchedulingActionTypes,
  UnavailActionTypes,
  SchedulingThunkResult,
  setScheduleDateBoundaries,
  notifyPosUsers,
  toggleNotifyModal,
  MODAL_STATUS,
};
